import { useCallback, useEffect, useRef, useState } from 'react'

import type { AgentCommand } from '../../lib/api/admin.ts'
import { AthenaError } from '../../lib/api/client.ts'
import { describeError } from '../../lib/api/messages.ts'
import { companies, machines } from '../../lib/api/resources.ts'
import type {
  CompanyIsolationPolicy,
  IsolationPolicyMode,
  IsolationPolicyModeInfo,
  MachineIsolationPolicy,
} from '../../lib/api/types.ts'
import { useAuth } from '../../lib/auth/AuthContext.tsx'
import {
  AUTOMATIC_WARNING,
  divergenceOffersResave,
  divergenceText,
  effectivePolicyLabel,
  isPolicyMode,
  policyOutcome,
} from '../../lib/commands/isolation.ts'
import { isAwaitingAgent, isConfirmed } from '../../lib/commands/semantics.ts'
import { useCommandTracking } from '../../lib/commands/useCommandTracking.ts'
import { formatDayClock } from '../../lib/format.ts'
import { useAction } from '../../lib/useAction.ts'
import { useResource } from '../../lib/useResource.ts'
import { Button } from '../primitives/Button.tsx'
import { Dialog } from '../primitives/Dialog.tsx'
import { SelectField } from '../primitives/Field.tsx'
import { ErrorState, LoadingRow, Notice } from '../primitives/States.tsx'
import { useToast } from '../primitives/Toast.tsx'
import { CommandStateBadge, ReleaseStatus } from '../commands/CommandState.tsx'

/**
 * O regime de isolamento de UMA máquina. Puramente apresentacional, para o
 * render check: afirma só o `effective` (nunca o `desired`), diz por que o
 * pedido diverge e mostra o seletor apenas para `platform_admin`.
 */
export function IsolationPolicyView({
  policy,
  isAdmin,
  onSave,
  onTrack,
}: {
  policy: MachineIsolationPolicy
  isAdmin: boolean
  /** Abre a confirmação do regime escolhido. */
  onSave: (mode: IsolationPolicyModeInfo) => void
  /** Acompanha o comando do pedido (ex.: o já entregue de um 409). */
  onTrack: (command: AgentCommand) => void
}) {
  const [choice, setChoice] = useState<string>(policy.desired?.mode ?? policy.effective.mode)
  const chosen = policy.options.find((o) => o.mode === choice) ?? null
  const divergence = divergenceText(policy)
  const resave = !policy.in_sync && divergenceOffersResave(policy.divergence)

  return (
    <div className="space-y-3">
      <div className="border-l-2 border-line-strong bg-inset px-3 py-2">
        <span className="text-micro font-medium uppercase tracking-label text-fg-secondary">Em vigor</span>
        <p className="mt-0.5 text-base text-fg">{effectivePolicyLabel(policy)}</p>
        {policy.confirmed_at !== null && (
          <p className="mt-0.5 text-micro text-fg-muted">
            confirmado pelo agente em <span className="tnum font-mono">{formatDayClock(policy.confirmed_at)}</span>
          </p>
        )}
      </div>

      {!policy.in_sync && (
        <Notice tone={policy.divergence === 'failed' ? 'crit' : 'warn'}>
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>
              Pedido: <strong>{policy.desired?.label ?? '—'}</strong> — {divergence}.
            </span>
            {policy.desired_command !== null && <CommandStateBadge state={policy.desired_command.state} />}
            {policy.desired_command !== null && (
              <button type="button" className="underline" onClick={() => onTrack(policy.desired_command!)}>
                Acompanhar
              </button>
            )}
          </span>
        </Notice>
      )}

      {policy.company_default !== null && (
        <p className="text-micro text-fg-muted">
          Padrão da empresa para máquinas novas: <span className="text-fg-secondary">{policy.company_default.label}</span>
        </p>
      )}

      {isAdmin ? (
        <div className="flex flex-wrap items-end gap-2 border-t border-line-subtle pt-3">
          <SelectField
            label="Regime"
            value={choice}
            onValueChange={setChoice}
            options={policy.options.map((o) => ({ value: o.mode, label: o.label }))}
            className="w-56"
          />
          <Button variant="primary" disabled={chosen === null} onClick={() => chosen !== null && onSave(chosen)}>
            {resave ? 'Salvar de novo' : 'Salvar'}
          </Button>
        </div>
      ) : (
        <p className="text-micro text-fg-muted">Só administradores trocam o regime de isolamento.</p>
      )}
    </div>
  )
}

type Phase =
  | { kind: 'view' }
  | { kind: 'confirm'; mode: IsolationPolicyModeInfo }
  | { kind: 'tracking'; id: string; initial: AgentCommand | null }

/** Diálogo "Regime de isolamento" do detalhe da máquina. */
export function MachineIsolationPolicyDialog({
  machine,
  onClose,
}: {
  machine: { id: string; label: string }
  onClose: () => void
}) {
  const { isAdmin } = useAuth()
  const fetcher = useCallback((signal: AbortSignal) => machines.isolationPolicy(machine.id, signal), [machine.id])
  const { data, error, reload } = useResource(fetcher)
  const [phase, setPhase] = useState<Phase>({ kind: 'view' })

  const back = (): void => {
    setPhase({ kind: 'view' })
    reload()
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Regime de isolamento"
      description={<span className="font-medium text-fg">{machine.label}</span>}
      wide
    >
      {phase.kind === 'tracking' ? (
        <PolicyTracking commandId={phase.id} initial={phase.initial} onBack={back} onClose={onClose} />
      ) : error !== null ? (
        <ErrorState error={error} onRetry={reload} />
      ) : data === null ? (
        <LoadingRow />
      ) : phase.kind === 'confirm' ? (
        <PolicyConfirm
          machineId={machine.id}
          mode={phase.mode}
          current={data}
          onAccepted={(command) => setPhase({ kind: 'tracking', id: command.id, initial: command })}
          onAlreadyOpen={(command) => setPhase({ kind: 'tracking', id: command.id, initial: command })}
          onCancel={back}
        />
      ) : (
        <IsolationPolicyView
          key={`${data.desired_command?.id ?? ''}|${data.effective.mode}`}
          policy={data}
          isAdmin={isAdmin}
          onSave={(mode) => setPhase({ kind: 'confirm', mode })}
          onTrack={(command) => setPhase({ kind: 'tracking', id: command.id, initial: command })}
        />
      )}
    </Dialog>
  )
}

function PolicyConfirm({
  machineId,
  mode,
  current,
  onAccepted,
  onAlreadyOpen,
  onCancel,
}: {
  machineId: string
  mode: IsolationPolicyModeInfo
  current: MachineIsolationPolicy
  onAccepted: (command: AgentCommand) => void
  onAlreadyOpen: (command: AgentCommand) => void
  onCancel: () => void
}) {
  const action = useAction()
  const [reason, setReason] = useState('')
  const valid = isPolicyMode(mode.mode)

  const submit = (): void => {
    if (!valid) return
    action.run(async () => {
      try {
        const trimmed = reason.trim()
        const command = await machines.setIsolationPolicy(machineId, {
          mode: mode.mode as IsolationPolicyMode,
          ...(trimmed === '' ? {} : { reason: trimmed }),
        })
        onAccepted(command)
      } catch (cause) {
        // O pedido anterior JÁ foi entregue e aguarda confirmação: mostre-o.
        if (
          cause instanceof AthenaError &&
          cause.code === 'agent_command_already_open' &&
          current.desired_command !== null
        ) {
          onAlreadyOpen(current.desired_command)
          return
        }
        throw cause
      }
    })
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-fg">
        Trocar de <strong>{effectivePolicyLabel(current)}</strong> para <strong>{mode.label}</strong>?
      </p>
      {mode.mode === 'automatic' && <Notice tone="crit">{AUTOMATIC_WARNING}</Notice>}

      <label className="flex flex-col gap-1">
        <span className="text-micro font-medium uppercase tracking-label text-fg-secondary">Justificativa</span>
        <textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          maxLength={512}
          rows={2}
          className="w-full border border-line-strong bg-inset px-2 py-1 text-base text-fg placeholder:text-fg-muted"
          placeholder="opcional"
        />
      </label>

      <div className="flex items-center justify-end gap-2 border-t border-line-subtle pt-3">
        {action.error !== null && (
          <p role="alert" className="mr-auto text-sm leading-snug text-crit">
            {describeError(action.error)}
          </p>
        )}
        <Button variant="ghost" onClick={onCancel}>
          Voltar
        </Button>
        <Button
          variant={mode.mode === 'automatic' ? 'danger' : 'primary'}
          busy={action.busy}
          disabled={!valid}
          onClick={submit}
        >
          Salvar {mode.label}
        </Button>
      </div>
    </div>
  )
}

function PolicyTracking({
  commandId,
  initial,
  onBack,
  onClose,
}: {
  commandId: string
  initial: AgentCommand | null
  onBack: () => void
  onClose: () => void
}) {
  const toast = useToast()
  const { command, error, refresh } = useCommandTracking(commandId, initial)
  const announced = useRef(false)
  // Só anuncia se a confirmação aconteceu DIANTE do operador.
  const sawAwaiting = useRef(false)

  const state = command?.state ?? null
  if (state !== null && isAwaitingAgent(state)) sawAwaiting.current = true
  const confirmed = state !== null && isConfirmed(state)

  useEffect(() => {
    if (!confirmed || announced.current || !sawAwaiting.current) return
    announced.current = true
    toast.success('Regime de isolamento confirmado pelo agente.')
  }, [confirmed, toast])

  return (
    <div className="space-y-3">
      {command === null ? (
        error !== null ? (
          <ErrorState error={error} onRetry={refresh} />
        ) : (
          <LoadingRow label="Lendo o comando…" />
        )
      ) : (
        <>
          <ReleaseStatus command={command} outcome={policyOutcome(command.state)} />
          {error !== null && (
            <Notice tone="warn">
              Não foi possível atualizar o estado: {describeError(error)}{' '}
              <button type="button" className="underline" onClick={refresh}>
                Tentar de novo
              </button>
            </Notice>
          )}
        </>
      )}
      <div className="flex items-center justify-end gap-2 border-t border-line-subtle pt-3">
        <span className="mr-auto font-mono text-micro text-fg-muted">comando {commandId}</span>
        <Button variant="ghost" onClick={onBack}>
          Ver regime
        </Button>
        <Button variant={confirmed ? 'primary' : 'ghost'} onClick={onClose}>
          Fechar
        </Button>
      </div>
    </div>
  )
}

// ── empresa: padrão para máquinas novas ─────────────────────────────────────

const NO_DEFAULT = ''

/**
 * O padrão de UMA empresa para as máquinas novas. Puramente apresentacional,
 * para o render check. Não muda as máquinas existentes — e diz isso.
 */
export function CompanyIsolationPolicyView({
  policy,
  isAdmin,
  busy = false,
  error = null,
  onSave,
}: {
  policy: CompanyIsolationPolicy
  isAdmin: boolean
  busy?: boolean
  error?: string | null
  onSave: (mode: IsolationPolicyMode | null) => void
}) {
  const [choice, setChoice] = useState<string>(policy.mode?.mode ?? NO_DEFAULT)
  const options = [
    { value: NO_DEFAULT, label: 'Sem padrão (só recomendar)' },
    ...policy.options.map((o) => ({ value: o.mode, label: o.label })),
  ]
  const changed = choice !== (policy.mode?.mode ?? NO_DEFAULT)

  return (
    <div className="space-y-3">
      <Notice tone="neutral">As máquinas que já existem não mudam.</Notice>
      <p className="text-sm text-fg">
        Atual:{' '}
        {policy.mode === null ? (
          <strong>Sem padrão (só recomendar)</strong>
        ) : (
          <>
            <strong>{policy.mode.label}</strong>
            {policy.set_at !== null && (
              <span className="text-micro text-fg-muted">
                {' '}
                · definido em <span className="tnum font-mono">{formatDayClock(policy.set_at)}</span>
              </span>
            )}
          </>
        )}
      </p>

      {isAdmin ? (
        <>
          <SelectField
            label="Regime de isolamento das máquinas novas"
            value={choice}
            onValueChange={setChoice}
            options={options}
            className="max-w-72"
          />
          {choice === 'automatic' && changed && <Notice tone="crit">{AUTOMATIC_WARNING}</Notice>}
          <div className="flex items-center justify-end gap-2 border-t border-line-subtle pt-3">
            {error !== null && (
              <p role="alert" className="mr-auto text-sm leading-snug text-crit">
                {error}
              </p>
            )}
            <Button
              variant={choice === 'automatic' ? 'danger' : 'primary'}
              busy={busy}
              disabled={!changed || (choice !== NO_DEFAULT && !isPolicyMode(choice))}
              onClick={() => onSave(choice === NO_DEFAULT ? null : (choice as IsolationPolicyMode))}
            >
              Salvar padrão
            </Button>
          </div>
        </>
      ) : (
        <p className="text-micro text-fg-muted">Só administradores definem o padrão da empresa.</p>
      )}
    </div>
  )
}

export function CompanyIsolationPolicyDialog({
  company,
  onClose,
}: {
  company: { id: string; name: string }
  onClose: () => void
}) {
  const { isAdmin } = useAuth()
  const toast = useToast()
  const action = useAction()
  const fetcher = useCallback((signal: AbortSignal) => companies.isolationPolicy(company.id, signal), [company.id])
  const { data, error, reload } = useResource(fetcher)

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Regime de isolamento das máquinas novas"
      description={<span className="font-medium text-fg">{company.name}</span>}
      wide
    >
      {error !== null ? (
        <ErrorState error={error} onRetry={reload} />
      ) : data === null ? (
        <LoadingRow />
      ) : (
        <CompanyIsolationPolicyView
          key={`${data.mode?.mode ?? ''}|${data.set_at ?? ''}`}
          policy={data}
          isAdmin={isAdmin}
          busy={action.busy}
          error={action.error === null ? null : describeError(action.error)}
          onSave={(mode) =>
            action.run(async () => {
              const saved = await companies.setIsolationPolicy(company.id, { mode })
              toast.success(
                saved.mode === null
                  ? 'Padrão removido: as máquinas novas só recomendam.'
                  : `Padrão das máquinas novas: ${saved.mode.label}.`,
              )
              reload()
            })
          }
        />
      )}
      {data !== null && (
        <div className="mt-3 flex justify-end">
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </div>
      )}
    </Dialog>
  )
}
